import { FunctionComponent, SVGProps, useLayoutEffect, useRef } from 'react';

export type LocalIconProps = SVGProps<SVGSVGElement> & {
  containerClassName?: string;
  'data-testid'?: string;
};

let localIconScopeSeq = 0;

const REFERENCE = /url\(#([^)]+)\)|^#(.+)$/g;

export const scopeSvgIds = (root: Element) => {
  const owners = root.querySelectorAll('svg [id]');
  if (owners.length === 0) {
    return;
  }
  const suffix = `-${++localIconScopeSeq}`;
  const renamed = new Map<string, string>();
  owners.forEach(owner => {
    renamed.set(owner.id, `${owner.id}${suffix}`);
    owner.id = `${owner.id}${suffix}`;
  });
  root.querySelectorAll('*').forEach(element => {
    Array.from(element.attributes).forEach(attribute => {
      const next = attribute.value.replace(
        REFERENCE,
        (match, urlId?: string, hrefId?: string) => {
          const id = urlId ?? hrefId ?? '';
          const target = renamed.get(id);
          if (!target) {
            return match;
          }
          return urlId ? `url(#${target})` : `#${target}`;
        },
      );
      if (next !== attribute.value) {
        element.setAttribute(attribute.name, next);
      }
    });
  });
};

export const createLocalIcon = (
  Svg: FunctionComponent<SVGProps<SVGSVGElement>>,
): FunctionComponent<LocalIconProps> => {
  const LocalIcon: FunctionComponent<LocalIconProps> = ({
    containerClassName,
    'data-testid': dataTestId,
    ...svgProps
  }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      if (wrapperRef.current) {
        scopeSvgIds(wrapperRef.current);
      }
    }, []);
    return (
      <div
        className={`${containerClassName ?? ''} cdn-svg-icon`}
        data-testid={dataTestId}
      >
        <div ref={wrapperRef} className="cdn-svg-icon-inner">
          <Svg role="img" {...svgProps} />
        </div>
      </div>
    );
  };
  LocalIcon.displayName = 'LocalIcon';
  return LocalIcon;
};

export type LocalImageProps = {
  className?: string;
  containerClassName?: string;
  onLoaded?: () => void;
  'data-testid'?: string;
};

export const createLocalImage = (
  url: string,
): FunctionComponent<LocalImageProps> => {
  const LocalImage: FunctionComponent<LocalImageProps> = ({
    className,
    containerClassName,
    onLoaded,
    'data-testid': dataTestId,
  }) => (
    <div
      className={`${containerClassName ?? ''} cdn-svg-icon`}
      data-testid={dataTestId}
    >
      <div className="cdn-svg-icon-inner">
        <img
          src={url}
          alt=""
          className={className}
          onLoad={onLoaded}
          onError={onLoaded}
        />
      </div>
    </div>
  );
  LocalImage.displayName = 'LocalImage';
  return LocalImage;
};
